/**
 * Single source of truth for Client form configuration.
 * Shared by NewClient / EditClient / CreateClientFromRequest.
 */

export const CLIENT_COLORS = [
  { name: 'Sky', hex: '#0284c7' },
  { name: 'Gold', hex: '#B79043' },
  { name: 'Emerald', hex: '#059669' },
  { name: 'Indigo', hex: '#4f46e5' },
  { name: 'Rose', hex: '#e11d48' },
  { name: 'Orange', hex: '#ea580c' },
  { name: 'Red', hex: '#E33724' },
];

/**
 * Module catalogue, grouped by functional category for the module picker UI.
 * `id` values MUST match the backend module strings.
 */
export const CLIENT_MODULE_GROUPS = [
  {
    id: 'core',
    label: 'Noyau',
    modules: [
      { id: 'TABLEAU_DE_BORD', label: 'Tableau de bord' },
      { id: 'CONTEXTE', label: 'Contexte' },
      { id: 'PROCESSUS', label: 'Processus' },
      { id: 'OBJECTIFS', label: 'Objectifs' },
      { id: 'AXE_STRATEGIQUE', label: 'Axe stratégique' },
    ],
  },
  {
    id: 'hse',
    label: 'HSE / Qualité',
    modules: [
      { id: 'RISQUES', label: 'Risques' },
      { id: 'RISQUES_MARSA', label: 'Risques Marsa' },
      { id: 'PLANS_D_ACTIONS', label: "Plans d'actions" },
      { id: 'SANTE_SECURITE', label: 'Santé sécurité' },
      { id: 'ENVIRONNEMENT', label: 'Environnement' },
      { id: 'NON_CONFORMITE', label: 'Non-conformité' },
    ],
  },
  {
    id: 'audits',
    label: 'Audits & Conformité',
    modules: [
      { id: 'AUDITS_INTERNE', label: 'Audits interne' },
      { id: 'INTERNAL_AUDIT_ASSIST', label: 'Audit interne assisté (BetterAssist)' },
      { id: 'REVUES_DE_DIRECTION', label: 'Revues de direction' },
      { id: 'EXIGENCES_LEGALES_ET_REGLEMENTAIRES', label: 'Exigences légales' },
      { id: 'DOCUMENTS', label: 'Documents' },
      { id: 'EQUIPEMENT', label: 'Équipement' },
    ],
  },
  {
    id: 'ai',
    label: 'Better Suite',
    modules: [
      { id: 'BETTER_CHART', label: 'Better Chart' },
      { id: 'DALUX', label: 'Better Chart DALUX' },
      { id: 'BETTER_ASSIST', label: 'Better Assist' },
      { id: 'BETTER_CONNECT', label: 'Better Connect' },
      { id: 'WHATSAPP', label: 'WhatsApp' },
    ],
  },
];

export const ALL_CLIENT_MODULES = CLIENT_MODULE_GROUPS.flatMap((g) =>
  g.modules.map((m) => ({ ...m, groupId: g.id, groupLabel: g.label })),
);

/**
 * Licence — pseudo-module for the client's own BPMN / custom modules. It can be
 * kept working after expiry like any module; same code as the backend.
 */
export const CUSTOM_MODULES_CODE = 'MODULES_PERSONNALISES';

export const moduleLabel = (code) =>
  code === CUSTOM_MODULES_CODE
    ? 'Modules personnalisés'
    : ALL_CLIENT_MODULES.find((m) => m.id === code)?.label || code;

/** Date → "YYYY-MM-DD" from its local calendar day (never toISOString: UTC shifts the day). */
export function toYmd(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Licence fields for the create/update multipart body. On-premise clients are
 * skipped: their licence lives in their own database and is signed offline.
 */
export function appendLicenseFields(formData, data, keptModules) {
  if (data.isOnPremise) return;
  formData.append('licenseUnlimited', data.licenseUnlimited ? 'true' : 'false');
  formData.append('licenseExpiryMode', data.licenseExpiryMode || 'full');
  if (data.licenseExpiryMode === 'partial') {
    keptModules.forEach((m) => formData.append('licenseKeptModules[]', m));
  }
}

export const CLIENT_FORM_SECTIONS = [
  { id: 'general', label: 'Général' },
  { id: 'contract', label: 'Contrat' },
  { id: 'representative', label: 'Représentant' },
  { id: 'modules', label: 'Modules' },
  { id: 'branding', label: 'Identité' },
];
